import { isAttachmentDownloadUrl } from "@caliu/utils";
import { getExportMarkdown } from "./export/contentPipeline";

export interface AttachmentProjectionNoteInput {
  id: string;
  userId: string;
  content: string;
  createdAt: string;
  updatedAt: string;
}

export type AttachmentKind = "image" | "file";

export interface ProjectedAttachment {
  noteId: string;
  userId: string;
  position: number;
  /** "image" for `![alt](src)`, "file" for an attachment download link. */
  kind: AttachmentKind;
  alt: string;
  src: string;
  createdAt: string;
  updatedAt: string;
}

const MARKDOWN_IMAGE_PATTERN =
  /!\[([^\]]*)\]\(([^)\s]+)(?:\s+(?:"[^"]*"|'[^']*'))?\)/g;

// Plain markdown links (NOT preceded by `!`, which would make them images).
// Only links pointing at an attachment download URL count as file attachments.
const MARKDOWN_LINK_PATTERN =
  /(?<!!)\[([^\]]*)\]\(([^)\s]+)(?:\s+(?:"[^"]*"|'[^']*'))?\)/g;

function stripFrontmatter(markdown: string): string {
  if (!markdown.startsWith("---\n")) return markdown;
  const closingIndex = markdown.indexOf("\n---\n", 4);
  if (closingIndex === -1) return markdown;
  const body = markdown.slice(closingIndex + 5);
  return body.startsWith("\n") ? body.slice(1) : body;
}

export function projectNoteAttachments(
  note: AttachmentProjectionNoteInput,
): ProjectedAttachment[] {
  const markdown = stripFrontmatter(getExportMarkdown(note.content));
  const found: { index: number; kind: AttachmentKind; alt: string; src: string }[] = [];

  // Images: `![alt](src)` — any src counts.
  for (const match of markdown.matchAll(MARKDOWN_IMAGE_PATTERN)) {
    const [, alt = "", src = ""] = match;
    if (!src) continue;
    found.push({ index: match.index ?? 0, kind: "image", alt, src });
  }

  // File attachments: `[name](src)` where src is an attachment download URL.
  for (const match of markdown.matchAll(MARKDOWN_LINK_PATTERN)) {
    const [, alt = "", src = ""] = match;
    if (!src || !isAttachmentDownloadUrl(src)) continue;
    found.push({ index: match.index ?? 0, kind: "file", alt, src });
  }

  // Keep them in document order so positions are stable.
  found.sort((a, b) => a.index - b.index);

  return found.map((f, position) => ({
    noteId: note.id,
    userId: note.userId,
    position,
    kind: f.kind,
    alt: f.alt,
    src: f.src,
    createdAt: note.createdAt,
    updatedAt: note.updatedAt,
  }));
}
